import React, { useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Text, NumberInput, Bubble, Tooltip, Theme } from '@simplybenefits/sb-ui-library';

/**
 * 
 * @param {*} param0 
 * @returns 
 */
const HistoricalColumn = ({ title, data, onChange, errors }) => (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Text medium color={ 'primary' }>
            { title }
        </Text>

        <NumberInput
            label={ 'Premium Paid' }
            value={ data.premium }
            onChange={(event) => onChange('premium', event)}
            prefix
            error={Boolean(errors?.premium)}
            helperText={ errors?.premium }
        />       
        <NumberInput
            label={ 'Annualized Premium' }
            value={ data.annualizedPremium }
            onChange={(event) => onChange('annualizedPremium', event)}
            prefix
            error={Boolean(errors?.annualizedPremium)}
            helperText={ errors?.annualizedPremium }
        />
        <NumberInput
            label={ 'Claims Paid' }
            value={ data.paidClaims }
            onChange={(event) => onChange('paidClaims', event)}
            prefix
            error={Boolean(errors?.paidClaims)}
            helperText={ errors?.paidClaims }
        />
        <NumberInput
            label={ 'Stop Loss Claims' }
            value={ data.stopLossClaims }
            onChange={(event) => onChange('stopLossClaims', event)}
            prefix
            error={Boolean(errors?.stopLossClaims)}
            helperText={ errors?.stopLossClaims }
        />
        <NumberInput
            label={ 'Change in IBNR' }
            value={ data.changeInIbnr }
            onChange={(event) => onChange('changeInIbnr', event)}
            prefix
            error={Boolean(errors?.changeInIbnr)}
            helperText={ errors?.changeInIbnr }
        />
        <NumberInput
            label={ 'Stop Loss Charge' }
            value={ data.stopLossCharge }
            onChange={(event) => onChange('stopLossCharge', event)}
            prefix
            error={Boolean(errors?.stopLossCharge)}
            helperText={ errors?.stopLossCharge }
        />
        <NumberInput
            label={ 'Incurred Claims' }
            value={ data.incurredClaims }
            onChange={(event) => onChange('incurredClaims', event)}
            prefix
            error={Boolean(errors?.incurredClaims)}
            helperText={ errors?.incurredClaims }
        />
        <NumberInput
            label={ 'Projected Claims (Annualized)' }
            value={ data.projectedClaimsAnnualized }
            onChange={(event) => onChange('projectedClaimsAnnualized', event)}
            prefix
            error={Boolean(errors?.projectedClaimsAnnualized)}
            helperText={ errors?.projectedClaimsAnnualized }
        />
        <NumberInput
            label={ 'Paid Loss Ratio' }
            value={ data.paidLossRatio }
            onChange={(event) => onChange('paidLossRatio', event)}
            suffix
            error={Boolean(errors?.paidLossRatio)}
            helperText={ errors?.paidLossRatio }
        />
        <NumberInput
            label={ 'Adjusted Loss Ratio' }
            value={ data.adjustedLossRatio }
            onChange={(event) => onChange('adjustedLossRatio', event)}
            suffix
            error={Boolean(errors?.adjustedLossRatio)}
            helperText={ errors?.adjustedLossRatio }
        />
    </div>
);

/**
 * 
 * @param {*} param0 
 * @returns 
 */
const RenewalHistoricalAccordion = ({ year, renewalData, classMeta, handleInputChange, errors }) => {
    // Filter out periodStart and periodEnd when initializing state
    const validClassIDs = Object.keys(renewalData).filter((key) => key !== 'periodStart' && key !== 'periodEnd');

    const [ selectedClassID, setSelectedClassID ] = useState(validClassIDs[0]);

    const styles = {
        divider: { 
            width: '2px',
            margin: '35px 30px 0',
            backgroundColor: Theme.colors.lightGrey 
        }
    };
    
    return (
        <Accordion
            key={ year }
            style={{ boxShadow: 'none', padding: 0, minHeight: '60px' }} 
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ padding: 0 }}>
                <Text mediumLarge bold>
                    { year }
                </Text>
            </AccordionSummary>
            <AccordionDetails style={{ padding: 0, marginBottom: '18px' }}>
                <Text medium>
                    Fill in the Experience Analysis details below for the renewal period. Columns, classes, and/or years left empty will be excluded from the report.
                </Text>

                {
                    validClassIDs.map((classID) => (
                        <Tooltip
                            key={ classID }
                            tip={ classMeta[classID].description }
                            placement="top"
                            arrow
                            display="inline-block"
                        >
                            <Bubble
                                label={ classMeta[classID].title }
                                isSelected={ selectedClassID === classID }
                                onClick={() => setSelectedClassID(classID)}
                                clickable={ true }
                                customStyle={{ marginRight: '15px' }}
                            />
                        </Tooltip>
                    ))
                }

                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: '8px' }}>
                    <HistoricalColumn
                        title={ 'EHC' }
                        data={ renewalData[selectedClassID].ehc }
                        onChange={(field, event) => handleInputChange([ 'experienceAnalysis', 'historicalExperienceAnalysis', year, selectedClassID, 'ehc', field ], event)}
                        errors={ errors?.experienceAnalysis?.historicalExperienceAnalysis?.[year]?.[selectedClassID]?.ehc }
                    />

                    <div style={ styles.divider }></div>

                    <HistoricalColumn
                        title={ 'Dental' }
                        data={ renewalData[selectedClassID].dental }
                        onChange={(field, event) => handleInputChange([ 'experienceAnalysis', 'historicalExperienceAnalysis', year, selectedClassID, 'dental', field ], event)}
                        errors={ errors?.experienceAnalysis?.historicalExperienceAnalysis?.[year]?.[selectedClassID]?.dental }
                    />

                    <div style={ styles.divider }></div>

                    <HistoricalColumn
                        title={ 'STD' }
                        data={ renewalData[selectedClassID].std }
                        onChange={(field, event) => handleInputChange([ 'experienceAnalysis', 'historicalExperienceAnalysis', year, selectedClassID, 'std', field ], event)}
                        errors={ errors?.experienceAnalysis?.historicalExperienceAnalysis?.[year]?.[selectedClassID]?.std }
                    />
                </div>
            </AccordionDetails>
        </Accordion>
    );
};

export default RenewalHistoricalAccordion;